/* eslint-disable custom/no-restricted-translation-jsx-untranslated-children-text */
import React from 'react';

import { Typography } from '@mui/material';

import { useIntl } from '@kamernet/core/Intl';
import { Locale } from '@kamernet/core/Intl/types';
import { NavLink } from '@kamernet/core/Routing/NavLink';
import { ROUTES } from '@kamernet/core/Routing/routes';
import { ListingsSearchCategory } from '@kamernet/core/Routing/routes/ListingsSearch';

import styles from './Content.module.css';

export const ContentCountryShortTermRentals = () => {
  const { locale } = useIntl();
  const searchCategories = [ListingsSearchCategory.ShortTermRental];

  return locale === Locale.en ? (
    <React.Fragment>
      <Typography variant="h1" className={styles.title}>
        Short-Term Rentals in Netherlands
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        Short-term rentals in Netherlands are ideal for people who need a
        temporary place to stay without the long-term commitment of a
        traditional lease. Kamernet makes it easier to find rental options that
        fit your timeline, whether you’re visiting for a few weeks, relocating
        for work, or in transition between homes. With a variety of listings
        available, you can explore flexible rental terms that suit your needs
        and budget. These rentals are especially useful for internationals or
        students who want the freedom to settle quickly without investing in
        furniture or long-term arrangements.
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Why Short-Term Rentals Work Well in Netherlands
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        Short-term rentals are a practical solution for many people navigating
        the Dutch housing market. Whether you’re relocating for a temporary job,
        waiting for your permanent housing to become available, or exploring
        different cities before settling down, these rentals offer flexibility
        and peace of mind. Netherlands’ fast-paced rental market can make
        long-term leases difficult to secure, especially for newcomers. With a
        short-term rental, you avoid the pressure of locking into a long
        contract while still enjoying the comforts of a private, furnished
        space. For internationals, expats, or students, this means less
        bureaucracy, fewer upfront costs, and the freedom to move when needed.
        It’s a way to start fresh in a new place without being tied down.
      </Typography>
      <Typography variant="h3" className={styles.title}>
        A Smarter Way to Rent Short Term in Netherlands
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        Finding short-term rentals in Netherlands can be frustrating, especially
        when options are limited or costs are high. Most platforms that offer
        temporary stays charge per night, making even a few weeks cost as much
        as a hotel. Traditional agencies often ask for a full month’s rent as a
        commission, creating a barrier for those who simply need a place for a
        short period. Kamernet offers a more practical alternative. With just a
        one-time sign-up fee, users gain access to exclusive listings that
        aren’t available elsewhere. With no agency fees and direct contact with
        landlords, Kamernet gives you a more affordable and transparent way to
        find housing for shorter periods without compromising on quality or
        convenience.
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Why Choose Kamernet for Short Term Rentals?
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        Kamernet is the best website to find short stay rentals in Netherlands
        that are updated regularly. Thousands have successfully secured
        temporary accommodations through Kamernet, a trusted platform with years
        of experience and reviews that speak for themselves. While many
        short-term rental platforms leave renters frustrated by long wait times,
        high fees, or outdated listings, Kamernet gives you quick access, clear
        pricing, and reliable options that suit your needs. If you’re still
        looking for temporary housing, let Kamernet help you find the best short
        term rentals in Netherlands that feel like home.
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Popular Cities:
      </Typography>
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'amsterdam', searchCategories },
          Locale.en,
        )}
      >
        Amsterdam Short Term Rentals
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'rotterdam', searchCategories },
          Locale.en,
        )}
      >
        Rotterdam Short Term Rentals
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'den-haag', searchCategories },
          Locale.en,
        )}
      >
        Den Haag Short Term Rentals
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'eindhoven', searchCategories },
          Locale.en,
        )}
      >
        Eindhoven Short Term Rentals
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'groningen', searchCategories },
          Locale.en,
        )}
      >
        Groningen Short Term Rentals
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'utrecht', searchCategories },
          Locale.en,
        )}
      >
        Utrecht Short Term Rentals
      </NavLink.Anchor>
      <Typography variant="h3" className={styles.title}>
        Popular Neighbourhoods:
      </Typography>
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'amstelveen', street: 'uilenstede', searchCategories },
          Locale.en,
        )}
      >
        Uilenstede Short Term Rentals
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'amsterdam', street: 'bijlmerdreef', searchCategories },
          Locale.en,
        )}
      >
        Bijlmerdreef Short Term Rentals
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'rotterdam', street: 'zuidplein', searchCategories },
          Locale.en,
        )}
      >
        Zuidplein Short Term Rentals
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'leiden', street: 'klikspaanweg', searchCategories },
          Locale.en,
        )}
      >
        Klikspaanweg Short Term Rentals
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'diemen', street: 'rode-kruislaan', searchCategories },
          Locale.en,
        )}
      >
        Rode Kruislaan Short Term Rentals
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'den-haag', street: 'leyweg', searchCategories },
          Locale.en,
        )}
      >
        Leyweg Short Term Rentals
      </NavLink.Anchor>
    </React.Fragment>
  ) : (
    <React.Fragment>
      <Typography variant="h1" className={styles.title}>
        Tijdelijk Huren in Netherlands
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        Tijdelijk huren in Netherlands is ideaal voor mensen die voor een korte
        periode woonruimte nodig hebben, zonder vast te zitten aan een langdurig
        huurcontract. Kamernet helpt je eenvoudig een woning te vinden die past
        bij jouw planning, of je nu voor een paar weken op bezoek bent, verhuist
        voor werk of in een overgangsfase zit. Met een breed aanbod kun je
        kiezen uit flexibele huuropties die passen bij jouw wensen en budget.
        Tijdelijk wonen in Netherlands is vooral geschikt voor internationals of
        studenten die snel willen intrekken, zonder te investeren in meubels of
        langdurige verplichtingen.
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Waarom Tijdelijke Woningen Werken in Netherlands
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        Een tijdelijke woning huren in Netherlands is een slimme keuze voor
        iedereen die zich beweegt in de drukke Nederlandse woningmarkt. Of je nu
        werkt op projectbasis, wacht op een permanente woning of verschillende
        steden wilt verkennen voordat je een vaste plek kiest, tijdelijk wonen
        in Netherlands biedt je de flexibiliteit die je nodig hebt. De
        woningmarkt in Netherlands is snel en competitief, waardoor het lastig
        kan zijn om direct een langdurig contract te krijgen. Een tijdelijk huis
        huren in Netherlands geeft je de ruimte om zonder stress te starten,
        terwijl je wel geniet van privacy, comfort en een volledig gemeubileerde
        woning.
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Betaalbaar en Slim Tijdelijk Huren in Netherlands
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        Tijdelijk huren in Netherlands kan lastig zijn wanneer het aanbod
        beperkt is of de kosten hoog zijn. Veel platforms rekenen per nacht, wat
        snel oploopt. Traditionele makelaars brengen vaak een maand huur als
        commissie in rekening. Kamernet biedt een voordelig alternatief. Met
        slechts een eenmalige registratie krijg je toegang tot exclusieve
        advertenties zonder tussenpersonen. Dankzij direct contact met de
        verhuurder kun je tijdelijk wonen in Netherlands op een betaalbare en
        transparante manier, zonder concessies te doen aan kwaliteit of gemak.
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Waarom Kamernet de Beste Keuze Is voor Tijdelijke Verblijven
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        Kamernet is de ideale plek om een tijdelijke woning te huren in
        Netherlands. Het platform wordt regelmatig bijgewerkt en biedt
        betrouwbare woonruimtes die passen bij verschillende situaties.
        Duizenden mensen hebben via Kamernet succesvol tijdelijk huisvesting
        gevonden. Waar andere platforms traag zijn of verborgen kosten rekenen,
        biedt Kamernet duidelijke voorwaarden en snelle toegang tot beschikbare
        woningen. Als je nog zoekt naar een tijdelijke woning, dan helpt
        Kamernet je graag om een tijdelijk huis te huren in Netherlands waar je
        je meteen thuis voelt.
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Populaire steden:
      </Typography>
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'amsterdam', searchCategories },
          Locale.nl,
        )}
      >
        Tijdelijk huren Amsterdam
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'rotterdam', searchCategories },
          Locale.nl,
        )}
      >
        Tijdelijk huren Rotterdam
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'den-haag', searchCategories },
          Locale.nl,
        )}
      >
        Tijdelijk huren Den Haag
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'eindhoven', searchCategories },
          Locale.nl,
        )}
      >
        Tijdelijk huren Eindhoven
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'groningen', searchCategories },
          Locale.nl,
        )}
      >
        Tijdelijk huren Groningen
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'utrecht', searchCategories },
          Locale.nl,
        )}
      >
        Tijdelijk huren Utrecht
      </NavLink.Anchor>
      <Typography variant="h3" className={styles.title}>
        Populaire buurten:
      </Typography>
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'amstelveen', street: 'uilenstede', searchCategories },
          Locale.nl,
        )}
      >
        Tijdelijk huren Uilenstede
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'amsterdam', street: 'bijlmerdreef', searchCategories },
          Locale.nl,
        )}
      >
        Tijdelijk huren Bijlmerdreef
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'rotterdam', street: 'zuidplein', searchCategories },
          Locale.nl,
        )}
      >
        Tijdelijk huren Zuidplein
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'leiden', street: 'klikspaanweg', searchCategories },
          Locale.nl,
        )}
      >
        Tijdelijk huren Klikspaanweg
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'diemen', street: 'rode-kruislaan', searchCategories },
          Locale.nl,
        )}
      >
        Tijdelijk huren Rode Kruislaan
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'den-haag', street: 'leyweg', searchCategories },
          Locale.nl,
        )}
      >
        Tijdelijk huren Leyweg
      </NavLink.Anchor>
    </React.Fragment>
  );
};
